import { Routes, Route, Navigate } from 'react-router-dom';
import { PortalLoginPage } from '@/pages/portal/PortalLoginPage';
import PortalInvitationAcceptancePage from '@/pages/portal/PortalInvitationAcceptancePage';
import PortalLandingPage from '@/pages/portal/PortalLandingPage';
import { PortalForgotPasswordPage } from '@/pages/portal/PortalForgotPasswordPage';
import { PortalResetPasswordPage } from '@/pages/portal/PortalResetPasswordPage';
import { PortalMfaSetupPage } from '@/pages/portal/PortalMfaSetupPage';
import { PortalMfaEnrollmentPage } from '@/pages/portal/PortalMfaEnrollmentPage';
import { PortalMfaRecoverPage } from '@/pages/portal/PortalMfaRecoverPage';
import { PortalDashboardPage } from '@/pages/portal/PortalDashboardPage';
import { PortalAssessmentListPage } from '@/pages/portal/PortalAssessmentListPage';
import { PortalAssessmentPage } from '@/pages/portal/PortalAssessmentPage';
import { PortalAssessmentSubmittedPage } from '@/pages/portal/PortalAssessmentSubmittedPage';
import { PortalCertificationsPage } from '@/pages/portal/PortalCertificationsPage';
import { PortalProfilePage } from '@/pages/portal/settings/PortalProfilePage';
import { PortalSecurityPage } from '@/pages/portal/settings/PortalSecurityPage';
import { PortalTeamPage } from '@/pages/portal/settings/PortalTeamPage';
import { PortalSettingsLoginMethodsPage } from '@/pages/portal/settings/PortalSettingsLoginMethodsPage';
import { PortalOAuthCompletePage } from '@/pages/portal/PortalOAuthCompletePage';
import { PortalTenantDashboardPage } from '@/pages/portal/PortalTenantDashboardPage';
import { PortalTenantAssessmentsPage } from '@/pages/portal/PortalTenantAssessmentsPage';
import { PortalTenantCertificationsPage } from '@/pages/portal/PortalTenantCertificationsPage';
import { PortalLayout } from '@/components/layout/PortalLayout';
import { PortalSettingsLayout } from '@/components/layout/PortalSettingsLayout';
import { TenantScopeProvider } from '@/components/portal/TenantScopeProvider';
import { usePortalAuthStore } from '@/store/portal-auth.store';

function PortalProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isPortalAuthenticated } = usePortalAuthStore();
  if (!isPortalAuthenticated) return <Navigate to="/portal/login" replace />;
  return <>{children}</>;
}

export function PortalRouter() {
  return (
    <div className="min-h-screen bg-bg-base">
      <Routes>
        {/* Public portal routes */}
        <Route path="/portal/oauth-complete" element={<PortalOAuthCompletePage />} />
        <Route path="/portal/login" element={<PortalLoginPage />} />
        <Route path="/portal/invitations/:token" element={<PortalInvitationAcceptancePage />} />
        <Route path="/portal/forgot-password" element={<PortalForgotPasswordPage />} />
        <Route path="/portal/reset-password" element={<PortalResetPasswordPage />} />
        <Route path="/portal/mfa/recover" element={<PortalMfaRecoverPage />} />
        <Route path="/portal/mfa/enrollment" element={<PortalMfaEnrollmentPage />} />

        {/* Semi-protected: requires portal JWT (after accept-invitation or mfa/recover/confirm) */}
        <Route
          path="/portal/mfa/setup"
          element={
            <PortalProtectedRoute>
              <PortalMfaSetupPage />
            </PortalProtectedRoute>
          }
        />

        {/* Protected portal routes — inside PortalLayout */}
        <Route
          path="/portal"
          element={
            <PortalProtectedRoute>
              <PortalLayout />
            </PortalProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/portal/dashboard" replace />} />
          <Route path="home" element={<PortalLandingPage />} />
          <Route path="dashboard" element={<PortalDashboardPage />} />
          <Route path="assessments" element={<PortalAssessmentListPage />} />
          <Route path="assessments/:id" element={<PortalAssessmentPage />} />
          <Route path="assessments/:id/submitted" element={<PortalAssessmentSubmittedPage />} />
          <Route path="certifications" element={<PortalCertificationsPage />} />
          <Route path="settings" element={<PortalSettingsLayout />}>
            <Route index element={<Navigate to="/portal/settings/profile" replace />} />
            <Route path="profile" element={<PortalProfilePage />} />
            <Route path="security" element={<PortalSecurityPage />} />
            <Route path="team" element={<PortalTeamPage />} />
            <Route path="login-methods" element={<PortalSettingsLoginMethodsPage />} />
          </Route>

          {/* Sprint U.3 — tenant-scoped routes: slug resolved by TenantScopeProvider layout */}
          <Route path="tenants/:tenantSlug" element={<TenantScopeProvider />}>
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<PortalTenantDashboardPage />} />
            <Route path="assessments" element={<PortalTenantAssessmentsPage />} />
            <Route path="certifications" element={<PortalTenantCertificationsPage />} />
          </Route>
        </Route>

        <Route path="*" element={<Navigate to="/portal/login" replace />} />
      </Routes>
    </div>
  );
}
